import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { X, Plus, Save, RotateCcw } from "lucide-react";
import { toast } from "sonner";

/*
 * Admin-managed dropdown lists (industries, lifecycle stages, ...).
 * The keys must match the LOOKUP_KEYS dict on the backend.
 */
export default function LookupListsPanel() {
  const [lists, setLists] = useState(null);
  const [dirty, setDirty] = useState({});
  const [saving, setSaving] = useState({});
  const [newValues, setNewValues] = useState({});

  const load = async () => {
    try {
      const r = await api.get("/lookup-lists");
      setLists(r.data);
      setDirty({});
      setNewValues({});
    } catch {
      toast.error("Failed to load lookup lists");
    }
  };
  useEffect(() => { load(); }, []);

  if (!lists) return <div className="text-sm text-slate-500">Loading…</div>;

  const update = (key, values) => {
    setLists((prev) => ({ ...prev, [key]: { ...prev[key], values } }));
    setDirty((d) => ({ ...d, [key]: true }));
  };

  const addValue = (key) => {
    const v = (newValues[key] || "").trim();
    if (!v) return;
    const existing = lists[key].values.map((x) => x.toLowerCase());
    if (existing.includes(v.toLowerCase())) {
      toast.error("Already in the list");
      return;
    }
    update(key, [...lists[key].values, v]);
    setNewValues((n) => ({ ...n, [key]: "" }));
  };

  const removeAt = (key, idx) => {
    update(key, lists[key].values.filter((_, i) => i !== idx));
  };

  const editAt = (key, idx, value) => {
    update(key, lists[key].values.map((v, i) => (i === idx ? value : v)));
  };

  const save = async (key) => {
    setSaving((s) => ({ ...s, [key]: true }));
    try {
      const r = await api.put(`/lookup-lists/${key}`, { values: lists[key].values });
      setLists((prev) => ({ ...prev, [key]: { ...prev[key], values: r.data.values } }));
      setDirty((d) => ({ ...d, [key]: false }));
      toast.success(`${lists[key].label} saved`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Save failed");
    } finally {
      setSaving((s) => ({ ...s, [key]: false }));
    }
  };

  return (
    <div className="space-y-6 max-w-3xl" data-testid="lookup-lists-panel">
      <div>
        <h2 className="text-lg font-heading font-semibold text-slate-900">Dropdown lookup lists</h2>
        <p className="text-sm text-slate-500 mt-1">
          Manage the values that appear in Account dropdowns (Industry, Lifecycle stage). Every option
          must be unique. Removing a value leaves existing accounts on that value — they just won&apos;t
          be an option for new records.
        </p>
      </div>

      {Object.entries(lists).map(([key, list]) => (
        <Card key={key} className="border-slate-200" data-testid={`lookup-list-${key}`}>
          <CardHeader className="border-b border-slate-100 flex flex-row items-center justify-between">
            <CardTitle className="text-base font-heading">{list.label}</CardTitle>
            <div className="flex items-center gap-2">
              {dirty[key] && <span className="text-[10px] font-mono uppercase tracking-wider text-amber-700">Unsaved</span>}
              <Button
                variant="ghost"
                size="sm"
                onClick={() => load()}
                disabled={!dirty[key]}
                className="text-slate-500"
                title="Discard changes"
              >
                <RotateCcw className="h-3.5 w-3.5" />
              </Button>
              <Button
                onClick={() => save(key)}
                disabled={!dirty[key] || saving[key]}
                size="sm"
                className="bg-blue-600 hover:bg-blue-700 gap-1.5"
                data-testid={`lookup-save-${key}`}
              >
                <Save className="h-3.5 w-3.5" />
                {saving[key] ? "Saving…" : "Save"}
              </Button>
            </div>
          </CardHeader>
          <CardContent className="p-4 space-y-3">
            <div className="space-y-1.5" data-testid={`lookup-values-${key}`}>
              {list.values.length === 0 ? (
                <div className="text-sm text-slate-400 italic">No values yet — add one below.</div>
              ) : (
                list.values.map((v, i) => (
                  <div key={i} className="flex items-center gap-2">
                    <Input
                      value={v}
                      onChange={(e) => editAt(key, i, e.target.value)}
                      data-testid={`lookup-value-${key}-${i}`}
                      className="h-8"
                    />
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => removeAt(key, i)}
                      className="h-8 w-8 text-slate-400 hover:text-red-600"
                      data-testid={`lookup-remove-${key}-${i}`}
                    >
                      <X className="h-4 w-4" />
                    </Button>
                  </div>
                ))
              )}
            </div>
            <div className="flex items-center gap-2 border-t border-slate-100 pt-3">
              <Input
                placeholder={`Add a new ${list.label.toLowerCase().replace(/s$/, "")}…`}
                value={newValues[key] || ""}
                onChange={(e) => setNewValues((n) => ({ ...n, [key]: e.target.value }))}
                onKeyDown={(e) => e.key === "Enter" && addValue(key)}
                data-testid={`lookup-add-input-${key}`}
                className="h-8"
              />
              <Button
                onClick={() => addValue(key)}
                size="sm"
                variant="outline"
                data-testid={`lookup-add-btn-${key}`}
                className="gap-1"
              >
                <Plus className="h-3.5 w-3.5" /> Add
              </Button>
            </div>
          </CardContent>
        </Card>
      ))}
    </div>
  );
}
